import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle, 
  Info, 
  X 
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import clsx from 'clsx';

// --- Types ---

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label?: string; // Not strictly used in design but good for a11y
  onClick: () => void;
}

export interface ToastData {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
  action?: ToastAction; // If present, makes the whole toast interactive
}

interface ToastContextType {
  addToast: (toast: Omit<ToastData, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, action?: ToastAction, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

// --- Context ---

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// --- Component ---

const TOAST_VARIANTS = {
  success: {
    icon: CheckCircle,
    color: '#10B981', // fp-emerald-500
    borderColor: 'border-l-fp-emerald-500',
    iconColor: 'text-fp-emerald-500',
  },
  error: {
    icon: XCircle,
    color: '#DC2626', // fp-red-600
    borderColor: 'border-l-fp-red-600',
    iconColor: 'text-fp-red-600',
  },
  warning: {
    icon: AlertTriangle,
    color: '#CA8A04', // fp-amber-600
    borderColor: 'border-l-fp-amber-600',
    iconColor: 'text-fp-amber-600',
  },
  info: {
    icon: Info,
    color: '#0F766E', // fp-teal-700
    borderColor: 'border-l-fp-teal-700',
    iconColor: 'text-fp-teal-700',
  },
};

const ToastItem = ({ 
  toast, 
  onDismiss 
}: { 
  toast: ToastData; 
  onDismiss: (id: string) => void 
}) => {
  const variant = TOAST_VARIANTS[toast.type];
  const Icon = variant.icon;

  useEffect(() => {
    const duration = toast.duration || (toast.type === 'error' ? 8000 : 5000);
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast, onDismiss]);

  const handleClick = () => {
    if (toast.action) {
      toast.action.onClick();
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -20, x: 0 }} 
      animate={{ opacity: 1, y: 0, x: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={clsx(
        "relative w-full md:w-[360px] bg-white shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] rounded-lg overflow-hidden pointer-events-auto flex min-h-[48px]",
        "border-l-[4px]",
        variant.borderColor,
        toast.action && "cursor-pointer hover:bg-fp-slate-100 transition-colors"
      )}
      onClick={handleClick}
      role="alert"
    >
      <div className="flex items-start w-full p-3 md:p-4 gap-3">
        {/* Icon */}
        <div className={clsx("shrink-0 mt-0.5", variant.iconColor)}>
          <Icon size={20} strokeWidth={2} />
        </div>

        {/* Message */}
        <div className="flex-1 pt-[1px]">
          <p className="text-[14px] font-medium text-fp-navy-900 font-inter leading-tight">
            {toast.message}
          </p>
        </div>

        {/* Dismiss Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
          }}
          className="shrink-0 text-fp-slate-400 hover:text-fp-slate-700 transition-colors -mt-1 -mr-1 p-1 rounded-full hover:bg-slate-100"
          aria-label="Dismiss notification"
        >
          <X size={16} />
        </button>
      </div>
    </motion.div>
  );
};

// --- Provider ---

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((data: Omit<ToastData, 'id'>) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => {
      const newToasts = [{ ...data, id }, ...prev];
      return newToasts.slice(0, 3); // Max 3 visible
    });
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const success = useCallback((message: string, duration?: number) => {
    addToast({ type: 'success', message, duration });
  }, [addToast]);

  const error = useCallback((message: string, action?: ToastAction, duration?: number) => {
    addToast({ type: 'error', message, duration, action });
  }, [addToast]);

  const warning = useCallback((message: string, duration?: number) => {
    addToast({ type: 'warning', message, duration });
  }, [addToast]);

  const info = useCallback((message: string, duration?: number) => {
    addToast({ type: 'info', message, duration });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ addToast, removeToast, success, error, warning, info }}>
      {children}
      
      {/* Toast Container */}
      <div className={clsx(
        "fixed z-[9999] pointer-events-none flex flex-col gap-2",
        // Desktop Positioning
        "md:top-6 md:right-6 md:items-end",
        // Mobile Positioning
        "top-4 left-4 right-4 items-center"
      )}>
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <ToastItem key={toast.id} toast={toast} onDismiss={removeToast} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
